import { useGetBackProcedimentiIdInfoGenerali } from '@framework/api-client/api/backoffice/procedimenti/procedimenti.ts';
import { type CampoSemanticoProcedimento } from '@framework/api-client/api/backoffice/schemas/campoSemanticoProcedimento.ts';
import { type ProcedimentoConTask } from '@framework/api-client/api/backoffice/schemas/procedimentoConTask.ts';
import { getGetFrontAreaPubblicaRichiesteIdModuliIdQueryOptions } from '@framework/api-client/api/inquiries-compile/area-pubblica-osservazioni-controller/area-pubblica-osservazioni-controller.ts';
import { getGetFrontRichiesteIdModuliIdQueryOptions } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { useGetQuery } from '@framework/api-client/utils/hooks/useGetQuery.ts';
import { useProcedureDossierSemanticFields } from '@framework/common-web/schema/procedure/hooks/useProcedureDossierSemanticFields.ts';
import { useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useControlledAccessSubmission } from '../../submission/hooks/useControlledAccessSubmission.ts';
import { isPublicSubmission } from '../../submission/isPublicSubmission.ts';
import {
  type IUseFrontOfficeModuleResult,
  useFrontOfficeModule,
} from './useFrontOfficeModule.ts';

/**
 * Ritorna l'API corretta da chiamare in base al path fornito.
 * Nel caso di un path che contiene 'osservazioni' ritorna l'endpoint per le richieste pubbliche.
 * Altrimenti, ritorna l'endpoint per le richieste dell'utente.
 */
function getSubmissionModuleEndpoint(path: string) {
  if (isPublicSubmission(path)) {
    // GET/area-pubblica/richieste/{id}/moduli
    return getGetFrontAreaPubblicaRichiesteIdModuliIdQueryOptions;
  }

  // GET/richeste/{id}/moduli/{idModulo}
  return getGetFrontRichiesteIdModuliIdQueryOptions;
}

/**
 * Ritorna la richiesta in base all'id fornito, richiamando l'API
 * "privata" se si sta guardando una propria richiesta, o quella "pubblica"
 * se si cerca una richiesta pubblica.
 */
function useControlledAccessModule(props: {
  submissionId: string | null | undefined;
  moduleId: string | null | undefined;
  procedureId: string | null | undefined;
}) {
  const { submissionId, moduleId, procedureId } = props;
  const location = useLocation();

  const queryOptions = useMemo(
    () => getSubmissionModuleEndpoint(location.pathname),
    [location.pathname],
  );

  const result = useGetQuery(queryOptions, [
    submissionId ?? '<unknown>',
    moduleId ?? '<unknown>',
    { idIstanzaProcedimento: procedureId ?? undefined },
    {
      query: {
        keepPreviousData: true,
        enabled: Boolean(submissionId) && Boolean(moduleId),
      },
    },
  ]);

  return result;
}

export interface IUseModuleData {
  /** Indica il modulo salvato in questi dati */
  currentSubmissionModuleId?: string | null;
  module?: IUseFrontOfficeModuleResult | undefined;
  submission?: RichiestaDTO | null | undefined;
  dossier?: ProcedimentoConTask | null | undefined;
  dossierSemanticFields: CampoSemanticoProcedimento[] | null | undefined;
}

export function useModule(params: {
  submissionId: string | null | undefined;
  submissionModuleId: string | null | undefined;
  procedureId: string | null | undefined;
  readonly: boolean;
}) {
  const { submissionId, submissionModuleId, procedureId, readonly } = params;

  const { data, error, isLoading, isFetching } = useControlledAccessModule({
    submissionId,
    moduleId: submissionModuleId,
    procedureId,
  });

  const submissionRequest = useControlledAccessSubmission(submissionId);

  const module = useFrontOfficeModule({ data });

  const dossierResult = useGetBackProcedimentiIdInfoGenerali(
    submissionRequest.data?.idIstanzaProcedimentoPratica ?? '<unknown>',
    {
      query: {
        enabled: !!submissionRequest.data?.idIstanzaProcedimentoPratica,
      },
    },
  );

  const {
    data: dossierSemanticFields,
    error: dossierSemanticFieldsError,
    isLoading: isLoadingDossierSemanticFields,
    isFetching: isFetchingDossierSemanticFields,
  } = useProcedureDossierSemanticFields(dossierResult.data?.data);

  const dataRef = useRef<IUseModuleData>({
    module,
    submission: submissionRequest.data,
    dossier: dossierResult.data?.data,
    dossierSemanticFields,
    currentSubmissionModuleId: submissionModuleId,
  });

  // Se cambia il modulo richiesto, aggiorniamo i dati
  if (dataRef.current.currentSubmissionModuleId !== submissionModuleId) {
    dataRef.current = {
      module,
      submission: submissionRequest.data,
      dossier: dossierResult.data?.data,
      dossierSemanticFields,
      currentSubmissionModuleId: submissionModuleId,
    };
  }

  /**
   * Gestiamo il refetch come un'unica operazione, in modo da evitare di
   * avere dati aggiornati parzialmente.
   * Questo evita anche dei glitch visivi dovuti al re-rendering, e soprattutto
   * garantisce il corretto aggiornamento degli `initialValues` del `ModuleForm`.
   */
  if (
    !isFetching &&
    !dossierResult.isFetching &&
    !submissionRequest.isFetching &&
    !isFetchingDossierSemanticFields
  ) {
    dataRef.current = {
      module,
      submission: submissionRequest.data,
      dossier: dossierResult.data?.data,
      dossierSemanticFields,
      currentSubmissionModuleId: submissionModuleId,
    };
  }

  return {
    ...dataRef.current,
    error:
      error ??
      submissionRequest.error ??
      // In caso di readonly, non mostriamo l'errore del dossier né dei suoi
      // campi semantici
      // e.g. permettere l'accesso alla pagina a un utente con visibilità limitata
      // alla richiesta e non alla pratica. In readonly i dati saranno comunque raccolti
      // dal context snapshot.
      (!readonly && (dossierResult.error ?? dossierSemanticFieldsError)),
    isLoading:
      isLoading ||
      submissionRequest.isLoading ||
      dossierResult.isLoading ||
      isLoadingDossierSemanticFields,
    isFetching:
      isFetching ||
      submissionRequest.isFetching ||
      dossierResult.isFetching ||
      isFetchingDossierSemanticFields,
  };
}

export type IUseModule = ReturnType<typeof useModule>;
